import { Box, Grid } from '@factorialco/deprecated-design-system'
import {
  ContractVersionPageContractVersionFragment,
  JobPositionLevelDocument,
  JobPositionRoleDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import RowContainer from 'modules/contracts/components/ContractDetails/Section'
import BlankSection from 'modules/contracts/components/ContractDetails/Section/Blank'

import CurrentPosition from './CurrentPosition'
import JobDescription from './JobDescription'
import PreferredJobPosition from './PreferredJobPosition'
import RoleDescription from './RoleDescription'

interface Props {
  employeeId: number
  contractVersion: ContractVersionPageContractVersionFragment
  hasLegacyJobCatalog?: boolean
}

const JobPosition = ({ employeeId, contractVersion, hasLegacyJobCatalog }: Props) => {
  const canSee = contractVersion._policies?.seeJobTitle?.can ?? false
  const { data: jobPositionLevelData } = useQuery(JobPositionLevelDocument, {
    variables: {
      nodeUuid: contractVersion.jobCatalogTreeNodeUuid ?? '',
    },
    skip: !contractVersion.jobCatalogTreeNodeUuid,
  })
  const { data: jobPositionRoleData } = useQuery(JobPositionRoleDocument, {
    variables: {
      id: jobPositionLevelData?.jobCatalog?.treeNodesConnection?.nodes?.[0]?.ancestorId ?? 0,
    },
    skip: !contractVersion.jobCatalogTreeNodeUuid || !jobPositionLevelData,
  })

  if (!canSee) return null

  const canEdit = contractVersion._policies?.edit?.can ?? false
  const jobCatalogLevel = contractVersion.jobCatalogLevel
  const jobCatalogRole = contractVersion.jobCatalogLevel?.role
  const jobCatalogTreeNodeLevel = jobPositionLevelData?.jobCatalog?.treeNodesConnection?.nodes?.[0]
  const jobCatalogTreeNodeRole = jobPositionRoleData?.jobCatalog?.treeNodesConnection?.nodes?.[0]
  const hasCurrentPosition = !!jobCatalogLevel && !!jobCatalogRole
  const hasCurrentPositionWithJobCatalogTreeNode =
    !!jobCatalogTreeNodeLevel && !!jobCatalogTreeNodeRole
  const hasRoleDescription = !!jobCatalogRole?.description
  const hasJobDescription = !!contractVersion.esJobDescription
  const hasPreferredJobPosition = !!contractVersion.jobTitle
  const showCurrentPosition = hasCurrentPosition && hasLegacyJobCatalog
  const showCurrentPositionWithJobCatalogTreeNode =
    hasCurrentPositionWithJobCatalogTreeNode && !hasLegacyJobCatalog
  const jobCatalogTreeRoleDescription = jobCatalogTreeNodeRole?.description
  const showRoleDescription =
    canEdit ||
    (!hasLegacyJobCatalog && jobCatalogTreeRoleDescription) ||
    jobCatalogRole?.description
  const showJobDescription =
    (canEdit || contractVersion.esJobDescription) && contractVersion.country === 'es'
  const showPreferredJobPosition = canEdit || contractVersion.jobTitle
  const hasContent =
    hasCurrentPosition ||
    hasCurrentPositionWithJobCatalogTreeNode ||
    hasRoleDescription ||
    hasJobDescription ||
    hasPreferredJobPosition
  const showEmptySlate = canEdit && !hasContent

  if (!hasContent && !canEdit) return null

  const showLeftColumn =
    !showEmptySlate &&
    (showCurrentPosition || showRoleDescription || showCurrentPositionWithJobCatalogTreeNode)
  const jobCatalogDescription = () => {
    if (hasLegacyJobCatalog) {
      return jobCatalogRole?.description
    }
    return jobCatalogTreeRoleDescription
  }

  return (
    <RowContainer icon="briefcase" title={i18n.t('contract_versions.job_position.title')}>
      <Grid variant="fixed">
        {showLeftColumn && (
          <Box gridColSpan={{ xxs: 12, xs: 12, s: 6 }} gap="s32" paddingRight="s16">
            {showCurrentPositionWithJobCatalogTreeNode && (
              <CurrentPosition
                jobCatalogTreeNodeLevel={jobCatalogTreeNodeLevel}
                jobCatalogTreeNodeRole={jobCatalogTreeNodeRole}
              />
            )}
            {showCurrentPosition && (
              <CurrentPosition jobCatalogLevel={jobCatalogLevel} jobCatalogRole={jobCatalogRole} />
            )}

            {showRoleDescription && (
              <RoleDescription jobCatalogDescription={jobCatalogDescription()} />
            )}
          </Box>
        )}
        {!showEmptySlate && (
          <Box gridColSpan={{ xxs: 12, xs: 12, s: 6 }} gap="s32" paddingLeft="s16">
            {showPreferredJobPosition && (
              <PreferredJobPosition preferredJobPosition={contractVersion.jobTitle} />
            )}
            {showJobDescription && (
              <JobDescription jobDescription={contractVersion.esJobDescription ?? undefined} />
            )}
          </Box>
        )}
        {showEmptySlate && contractVersion.id && (
          <BlankSection
            employeeId={employeeId}
            contractVersionId={contractVersion.id}
            icon="inbox"
            title={i18n.t('contract_versions.job_position.blank_slate.title')}
            description={i18n.t('contract_versions.job_position.blank_slate.description')}
            buttonText={i18n.t('contract_versions.job_position.blank_slate.button')}
          />
        )}
      </Grid>
    </RowContainer>
  )
}
export default JobPosition
